import { useState } from 'react';
import { X, Plus, Trash2, Check } from 'lucide-react';
import type {
  ProjectoPLItem,
  ProjectoPLItemInsert,
  ProjectoItemPaymentStatus,
  ProjectoEntryKind,
  ProjectoPassthroughCost,
} from '../../types/projecto';
import { PROJECTO_ITEM_TYPES, PROJECTO_ITEM_TYPE_CONFIG } from '../../types/projecto';
import { Button } from '../ui/Button';
import { useModal } from '../../contexts/ModalContext';
import { reportFirstMissingField } from '../../utils/requiredFields';

interface Props {
  item?: ProjectoPLItem | null;
  defaultIsRevenue?: boolean;
  onSave: (data: Omit<ProjectoPLItemInsert, 'projecto_id'>) => Promise<void>;
  onClose: () => void;
}

const PAYMENT_STATUSES: ProjectoItemPaymentStatus[] = ['Pending', 'Invoiced', 'Paid', 'Cancelled'];
const PAYMENT_STATUS_LABELS: Record<string, string> = {
  Pending: 'Pendente',
  Invoiced: 'Faturado',
  Paid: 'Pago',
  Cancelled: 'Cancelado',
};

const EMPTY: Omit<ProjectoPLItemInsert, 'projecto_id'> = {
  item_type: 'Other',
  entry_kind: 'cost',
  is_revenue: false,
  name: '',
  description: null,
  quantity: 1,
  unit_price: 0,
  currency: 'EUR',
  margin_pct: 0,
  cost_commission_pct: 0,
  cost_commission_mode: 'percent',
  cost_commission_flat: 0,
  cost_billing_mode: 'absorbed',
  passthrough_breakdown: [],
  scheduled_date: null,
  actual_date: null,
  vendor_name: null,
  vendor_contact: null,
  contract_ref: null,
  link: null,
  observations: null,
  payment_status: 'Pending',
  expected_payment_date: null,
  actual_payment_date: null,
  display_order: 0,
};

function resolveKind(item?: ProjectoPLItem | null, fallbackIsRevenue = false): ProjectoEntryKind {
  if (item) {
    if (item.entry_kind) return item.entry_kind;
    if (item.is_revenue) return 'revenue';
    if (item.cost_billing_mode === 'reimbursed') return 'passthrough';
    return 'cost';
  }
  return fallbackIsRevenue ? 'revenue' : 'cost';
}

function genId(): string {
  return `c_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function fmtEur(n: number) {
  return n.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' });
}

function fmtNum(n: number) {
  return n % 1 === 0 ? String(n) : n.toFixed(2);
}

export function ProjectoPLItemModal({ item, defaultIsRevenue = false, onSave, onClose }: Props) {
  const initialKind = resolveKind(item, defaultIsRevenue);
  const [kind, setKind] = useState<ProjectoEntryKind>(initialKind);
  const [form, setForm] = useState<Omit<ProjectoPLItemInsert, 'projecto_id'>>(() =>
    item
      ? {
          item_type: item.item_type,
          entry_kind: initialKind,
          is_revenue: initialKind === 'revenue',
          name: item.name,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          currency: item.currency,
          margin_pct: item.margin_pct,
          cost_commission_pct: item.cost_commission_pct ?? 0,
          cost_commission_mode: item.cost_commission_mode ?? 'percent',
          cost_commission_flat: item.cost_commission_flat ?? 0,
          cost_billing_mode: initialKind === 'passthrough' ? 'reimbursed' : 'absorbed',
          passthrough_breakdown: Array.isArray(item.passthrough_breakdown) ? item.passthrough_breakdown : [],
          scheduled_date: item.scheduled_date,
          actual_date: item.actual_date,
          vendor_name: item.vendor_name,
          vendor_contact: item.vendor_contact,
          contract_ref: item.contract_ref,
          link: item.link,
          observations: item.observations,
          payment_status: item.payment_status,
          expected_payment_date: item.expected_payment_date,
          actual_payment_date: item.actual_payment_date,
          display_order: item.display_order,
        }
      : { ...EMPTY, entry_kind: initialKind, is_revenue: initialKind === 'revenue', cost_billing_mode: initialKind === 'passthrough' ? 'reimbursed' : 'absorbed' }
  );
  const { toast } = useModal();
  const [saving, setSaving] = useState(false);
  const [marginPctInput, setMarginPctInput] = useState(() => item?.margin_pct ? String(item.margin_pct) : '');
  const [marginAmtInput, setMarginAmtInput] = useState('');
  const [lastMarginEdit, setLastMarginEdit] = useState<'pct' | 'amt'>('pct');
  const [marginError, setMarginError] = useState('');

  const initialPct = item?.cost_commission_pct ?? 0;
  const initialFlat = item?.cost_commission_flat ?? 0;
  const initialMode = item?.cost_commission_mode ?? 'percent';
  const initialQty = item?.quantity ?? 1;
  const initialPerUnit = initialQty > 0 ? initialFlat / initialQty : 0;

  const [commPctInput, setCommPctInput] = useState(() => initialPct ? String(initialPct) : '');
  const [commFlatInput, setCommFlatInput] = useState(() => initialMode === 'flat' && initialFlat > 0 ? fmtNum(initialFlat) : '');
  const [commPerUnitInput, setCommPerUnitInput] = useState(() => initialMode === 'flat' && initialPerUnit > 0 ? fmtNum(initialPerUnit) : '');
  const [commError, setCommError] = useState('');

  const set = (updates: Partial<typeof form>) => setForm((f) => ({ ...f, ...updates }));

  const total = (form.quantity ?? 1) * (form.unit_price ?? 0);
  const marginAmount = kind === 'revenue' ? total * ((form.margin_pct ?? 0) / 100) : 0;
  const commissionMode = form.cost_commission_mode ?? 'percent';
  const commissionAmount = kind === 'cost'
    ? commissionMode === 'flat'
      ? (form.cost_commission_flat ?? 0)
      : total * ((form.cost_commission_pct ?? 0) / 100)
    : 0;
  const billableToClient = total + commissionAmount;
  const quantity = form.quantity ?? 1;

  const breakdown = (form.passthrough_breakdown ?? []) as ProjectoPassthroughCost[];
  const passthroughSpend = breakdown.reduce((s, c) => s + (Number(c.amount) || 0), 0);
  const passthroughCommission = Math.max(0, total - passthroughSpend);
  const passthroughNegative = passthroughSpend > total && total > 0;

  const setCommissionFromFlat = (flat: number) => {
    const safe = Math.max(0, flat);
    const pct = total > 0 ? Math.round((safe / total) * 10000) / 100 : 0;
    set({ cost_commission_flat: safe, cost_commission_pct: pct });
  };

  const setCommissionFromPct = (pct: number) => {
    const safe = Math.max(0, Math.min(100, pct));
    const flat = total > 0 ? Math.round(total * (safe / 100) * 100) / 100 : 0;
    set({ cost_commission_pct: safe, cost_commission_flat: flat });
  };

  const handleMarginPctChange = (raw: string) => {
    const cleaned = raw.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
    setMarginPctInput(cleaned);
    setLastMarginEdit('pct');
    setMarginError('');
    const num = parseFloat(cleaned);
    if (cleaned === '' || isNaN(num)) {
      set({ margin_pct: 0 });
      setMarginAmtInput('');
      return;
    }
    if (num > 100) { setMarginError('Deve ser entre 0 e 100'); return; }
    set({ margin_pct: num });
    if (total > 0) {
      const amt = total * (num / 100);
      setMarginAmtInput(fmtNum(amt));
    } else {
      setMarginAmtInput('');
    }
  };

  const handleMarginAmtChange = (raw: string) => {
    const cleaned = raw.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
    setMarginAmtInput(cleaned);
    setLastMarginEdit('amt');
    setMarginError('');
    const num = parseFloat(cleaned);
    if (cleaned === '' || isNaN(num) || total <= 0) {
      set({ margin_pct: 0 });
      setMarginPctInput('0');
      return;
    }
    const pct = (num / total) * 100;
    if (pct > 100) { setMarginError('Montante excede o total'); return; }
    const rounded = Math.round(pct * 100) / 100;
    set({ margin_pct: rounded });
    setMarginPctInput(fmtNum(rounded));
  };

  const handleCommPctChange = (raw: string) => {
    const cleaned = raw.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
    setCommPctInput(cleaned);
    setCommError('');
    const num = parseFloat(cleaned);
    if (cleaned === '' || isNaN(num)) {
      set({ cost_commission_pct: 0, cost_commission_flat: 0 });
      setCommFlatInput('');
      setCommPerUnitInput('');
      return;
    }
    if (num > 100) { setCommError('Deve ser entre 0 e 100'); return; }
    setCommissionFromPct(num);
    if (total > 0) {
      const flat = total * (num / 100);
      setCommFlatInput(fmtNum(Math.round(flat * 100) / 100));
      const perUnit = quantity > 0 ? flat / quantity : 0;
      setCommPerUnitInput(perUnit > 0 ? fmtNum(Math.round(perUnit * 100) / 100) : '');
    }
  };

  const handleCommFlatChange = (raw: string) => {
    const cleaned = raw.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
    setCommFlatInput(cleaned);
    setCommError('');
    const num = parseFloat(cleaned);
    if (cleaned === '' || isNaN(num)) {
      set({ cost_commission_flat: 0, cost_commission_pct: 0 });
      setCommPerUnitInput('');
      setCommPctInput('0');
      return;
    }
    setCommissionFromFlat(num);
    const perUnit = quantity > 0 ? num / quantity : 0;
    setCommPerUnitInput(perUnit > 0 ? fmtNum(Math.round(perUnit * 100) / 100) : '');
    if (total > 0) {
      const pct = Math.round((num / total) * 10000) / 100;
      setCommPctInput(fmtNum(pct));
    }
  };

  const handleCommPerUnitChange = (raw: string) => {
    const cleaned = raw.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
    setCommPerUnitInput(cleaned);
    setCommError('');
    const num = parseFloat(cleaned);
    if (cleaned === '' || isNaN(num)) {
      set({ cost_commission_flat: 0, cost_commission_pct: 0 });
      setCommFlatInput('');
      setCommPctInput('0');
      return;
    }
    const flat = num * quantity;
    setCommissionFromFlat(flat);
    setCommFlatInput(fmtNum(Math.round(flat * 100) / 100));
    if (total > 0) {
      const pct = Math.round((flat / total) * 10000) / 100;
      setCommPctInput(fmtNum(pct));
    }
  };

  const changeKind = (next: ProjectoEntryKind) => {
    setKind(next);
    if (next === 'revenue') {
      set({ entry_kind: 'revenue', is_revenue: true, cost_commission_pct: 0, cost_commission_flat: 0, cost_commission_mode: 'percent', cost_billing_mode: 'absorbed' });
      setCommPctInput('0'); setCommFlatInput(''); setCommPerUnitInput(''); setCommError('');
    } else if (next === 'passthrough') {
      set({ entry_kind: 'passthrough', is_revenue: false, cost_billing_mode: 'reimbursed', margin_pct: 0, cost_commission_pct: 0, cost_commission_flat: 0 });
      setMarginPctInput('0'); setMarginAmtInput('');
    } else {
      set({ entry_kind: 'cost', is_revenue: false, cost_billing_mode: 'absorbed', margin_pct: 0, passthrough_breakdown: [] });
      setMarginPctInput('0'); setMarginAmtInput('');
    }
  };

  const addBreakdownRow = () => {
    const next: ProjectoPassthroughCost[] = [...breakdown, { id: genId(), label: '', amount: 0, paid: false }];
    set({ passthrough_breakdown: next });
  };

  const updateBreakdown = (id: string, patch: Partial<ProjectoPassthroughCost>) => {
    const next = breakdown.map((c) => (c.id === id ? { ...c, ...patch } : c));
    set({ passthrough_breakdown: next });
  };

  const removeBreakdown = (id: string) => {
    set({ passthrough_breakdown: breakdown.filter((c) => c.id !== id) });
  };

  const handleSave = async () => {
    const blocked = reportFirstMissingField([
      {
        key: 'pl-item-name',
        missing: !(form.name ?? '').trim(),
        message: 'Preenche o Nome do item para poder guardar.',
      },
      {
        key: 'pl-item-passthrough',
        missing: kind === 'passthrough' && passthroughNegative,
        message: 'O detalhe do pass-through excede o valor total do item.',
      },
    ], toast.error);
    if (blocked) return;
    const safePct = Math.max(0, Math.min(100, form.margin_pct ?? 0));
    const safeCommPct = Math.max(0, Math.min(100, form.cost_commission_pct ?? 0));
    const safeCommFlat = Math.max(0, form.cost_commission_flat ?? 0);
    const finalForm: Omit<ProjectoPLItemInsert, 'projecto_id'> = {
      ...form,
      entry_kind: kind,
      is_revenue: kind === 'revenue',
      cost_billing_mode: kind === 'passthrough' ? 'reimbursed' : 'absorbed',
      margin_pct: safePct,
      cost_commission_pct: safeCommPct,
      cost_commission_flat: safeCommFlat,
      passthrough_breakdown: kind === 'passthrough' ? breakdown : [],
    };
    setSaving(true);
    try {
      await onSave(finalForm);
      onClose();
    } catch {
      setSaving(false);
    }
  };

  const inputCls = 'w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-shadow';
  const labelCls = 'block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5';

  const headerAccent =
    kind === 'revenue' ? { bg: 'bg-emerald-50/60', border: 'border-emerald-100', text: 'text-emerald-700', label: 'Serviço próprio — o que a agência vende ao cliente' }
    : kind === 'passthrough' ? { bg: 'bg-amber-50/60', border: 'border-amber-100', text: 'text-amber-700', label: 'Pass-through — reembolsado pelo cliente, apenas a comissão é lucro' }
    : { bg: 'bg-red-50/40', border: 'border-red-100', text: 'text-red-600', label: 'Custo — serviço/produto comprado para o evento' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 border-b ${headerAccent.border} rounded-t-2xl ${headerAccent.bg}`}>
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              {item ? 'Editar Item' : 'Adicionar Item'}
            </h2>
            <p className={`text-xs mt-0.5 font-medium ${headerAccent.text}`}>
              {headerAccent.label}
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-white/80 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 px-6 py-6 space-y-8">

          {/* Kind Selector + Basic Info */}
          <div className="space-y-5">
            <div className="flex gap-1 p-1 bg-gray-100 rounded-lg w-fit">
              <button
                onClick={() => changeKind('revenue')}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${kind === 'revenue' ? 'bg-white text-emerald-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Serviço Próprio
              </button>
              <button
                onClick={() => changeKind('passthrough')}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${kind === 'passthrough' ? 'bg-white text-amber-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Pass-through
              </button>
              <button
                onClick={() => changeKind('cost')}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${kind === 'cost' ? 'bg-white text-red-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Custo
              </button>
            </div>

            {/* Stacked: the category chips have a very wide max-content width and
                would otherwise squeeze the required Nome field down to a sliver,
                making it impossible to type a name (and to enable saving). */}
            <div className="space-y-4">
              <div>
                <label className={labelCls}>Nome <span className="text-red-500">*</span></label>
                <input
                  data-required-field="pl-item-name"
                  className={inputCls}
                  placeholder="ex: Design de Palco, Instagram Reel, Equipa no Evento"
                  value={form.name ?? ''}
                  onChange={(e) => set({ name: e.target.value })}
                />
              </div>
              <div>
                <label className={labelCls}>Categoria</label>
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {PROJECTO_ITEM_TYPES.map((t) => {
                    const cfg = PROJECTO_ITEM_TYPE_CONFIG[t];
                    const active = form.item_type === t;
                    return (
                      <button
                        key={t}
                        onClick={() => set({ item_type: t })}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                          active
                            ? `${cfg.bg} ${cfg.color} border-transparent`
                            : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {t}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="space-y-4">
            <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100 pb-2">
              {kind === 'passthrough' ? 'Valor Cobrado ao Cliente' : 'Preço'}
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className={labelCls}>Quantidade</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  className={inputCls}
                  value={form.quantity ?? 1}
                  onChange={(e) => set({ quantity: parseFloat(e.target.value) || 1 })}
                />
              </div>
              <div>
                <label className={labelCls}>Preço Unitário</label>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    step="100"
                    className={`${inputCls} pr-8`}
                    value={form.unit_price || ''}
                    onChange={(e) => set({ unit_price: parseFloat(e.target.value) || 0 })}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none">€</span>
                </div>
              </div>
              <div>
                <label className={labelCls}>Total</label>
                <div className={`${inputCls} bg-gray-50 font-semibold text-gray-900 cursor-default`}>
                  {fmtEur(total)}
                </div>
              </div>
            </div>

            {/* Margin (revenue) */}
            {kind === 'revenue' && (
              <div className="grid grid-cols-2 gap-4 pt-2">
                <div>
                  <label className={labelCls}>Margem %</label>
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="decimal"
                      className={`${inputCls} pr-7 tabular-nums ${marginError ? 'border-red-300 focus:ring-red-400' : ''}`}
                      placeholder="ex: 15.5"
                      value={marginPctInput}
                      onChange={(e) => handleMarginPctChange(e.target.value)}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none">%</span>
                  </div>
                  {marginError && <p className="mt-1 text-[11px] text-red-500">{marginError}</p>}
                </div>
                <div>
                  <label className={labelCls}>Montante da Margem</label>
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="decimal"
                      className={`${inputCls} pr-7 tabular-nums font-semibold text-emerald-700 ${total <= 0 ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : ''}`}
                      placeholder={total > 0 ? 'ex: 112.50' : 'Definir preço primeiro'}
                      value={lastMarginEdit === 'amt' ? marginAmtInput : (marginAmount > 0 ? fmtNum(marginAmount) : '')}
                      onChange={(e) => handleMarginAmtChange(e.target.value)}
                      disabled={total <= 0}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none">€</span>
                  </div>
                </div>
              </div>
            )}

            {/* Commission (cost) */}
            {kind === 'cost' && (
              <div className="space-y-4 pt-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                      Comissão sobre o Custo
                    </p>
                    <p className="text-[11px] text-gray-400 mt-0.5">
                      Cobrada ao cliente como receita.
                    </p>
                  </div>
                  <div className="flex gap-1 p-1 bg-gray-100 rounded-lg shrink-0">
                    <button
                      type="button"
                      onClick={() => set({ cost_commission_mode: 'percent' })}
                      className={`px-3 py-1 rounded-md text-xs font-semibold transition-all ${commissionMode === 'percent' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      Percentagem
                    </button>
                    <button
                      type="button"
                      onClick={() => set({ cost_commission_mode: 'flat' })}
                      className={`px-3 py-1 rounded-md text-xs font-semibold transition-all ${commissionMode === 'flat' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      Valor Fixo
                    </button>
                  </div>
                </div>

                {commissionMode === 'percent' ? (
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className={labelCls}>Comissão %</label>
                      <div className="relative">
                        <input
                          type="text"
                          inputMode="decimal"
                          className={`${inputCls} pr-7 tabular-nums ${commError ? 'border-red-300 focus:ring-red-400' : ''}`}
                          placeholder="ex: 15"
                          value={commPctInput}
                          onChange={(e) => handleCommPctChange(e.target.value)}
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none">%</span>
                      </div>
                      {commError && <p className="mt-1 text-[11px] text-red-500">{commError}</p>}
                    </div>
                    <div>
                      <label className={labelCls}>Montante Comissão</label>
                      <div className={`${inputCls} bg-gray-50 font-semibold text-emerald-700 tabular-nums cursor-default`}>
                        {fmtEur(commissionAmount)}
                      </div>
                    </div>
                    <div>
                      <label className={labelCls}>Faturável ao Cliente</label>
                      <div className={`${inputCls} bg-gray-50 font-semibold text-gray-900 cursor-default`}>
                        {fmtEur(billableToClient)}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className={labelCls}>Markup / Unidade</label>
                      <div className="relative">
                        <input
                          type="text"
                          inputMode="decimal"
                          className={`${inputCls} pr-8 tabular-nums`}
                          placeholder={quantity > 1 ? 'ex: 100' : 'Opcional'}
                          value={commPerUnitInput}
                          onChange={(e) => handleCommPerUnitChange(e.target.value)}
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none">€/u</span>
                      </div>
                    </div>
                    <div>
                      <label className={labelCls}>Comissão Total</label>
                      <div className="relative">
                        <input
                          type="text"
                          inputMode="decimal"
                          className={`${inputCls} pr-7 tabular-nums font-semibold text-emerald-700`}
                          placeholder="ex: 1200"
                          value={commFlatInput}
                          onChange={(e) => handleCommFlatChange(e.target.value)}
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none">€</span>
                      </div>
                    </div>
                    <div>
                      <label className={labelCls}>Faturável ao Cliente</label>
                      <div className={`${inputCls} bg-gray-50 font-semibold text-gray-900 cursor-default`}>
                        {fmtEur(billableToClient)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Pass-through breakdown */}
          {kind === 'passthrough' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b border-gray-100 pb-2">
                <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
                  Custos Reais (Breakdown)
                </h3>
                <button
                  type="button"
                  onClick={addBreakdownRow}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold text-amber-700 bg-amber-50 hover:bg-amber-100 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" /> Adicionar linha
                </button>
              </div>
              <p className="text-[11px] text-gray-400">
                Lista o que foi efetivamente gasto. Comissão = Cobrado − Gasto.
              </p>

              {breakdown.length === 0 ? (
                <div className="text-xs text-gray-400 italic py-4 px-3 border border-dashed border-gray-200 rounded-lg text-center">
                  Sem custos. Clique "Adicionar linha" para listar cada despesa.
                </div>
              ) : (
                <div className="rounded-lg border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Descrição</th>
                        <th className="px-3 py-2 text-right text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-28">Montante</th>
                        <th className="px-3 py-2 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-16">Pago</th>
                        <th className="w-10" />
                      </tr>
                    </thead>
                    <tbody>
                      {breakdown.map((row) => (
                        <tr key={row.id} className="border-t border-gray-100">
                          <td className="px-3 py-2">
                            <input
                              className="w-full px-2 py-1.5 text-sm border border-transparent hover:border-gray-200 focus:border-amber-300 rounded focus:outline-none focus:ring-1 focus:ring-amber-200"
                              placeholder="ex: Matilde, Aluguer sala"
                              value={row.label}
                              onChange={(e) => updateBreakdown(row.id, { label: e.target.value })}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <div className="relative">
                              <input
                                type="number"
                                min="0"
                                step="50"
                                className="w-full px-2 py-1.5 pr-6 text-sm text-right tabular-nums border border-transparent hover:border-gray-200 focus:border-amber-300 rounded focus:outline-none focus:ring-1 focus:ring-amber-200"
                                placeholder="0"
                                value={row.amount || ''}
                                onChange={(e) => updateBreakdown(row.id, { amount: parseFloat(e.target.value) || 0 })}
                              />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none">€</span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-center">
                            <button
                              type="button"
                              onClick={() => updateBreakdown(row.id, { paid: !row.paid })}
                              className={`inline-flex items-center justify-center w-6 h-6 rounded-md transition-all ${
                                row.paid
                                  ? 'bg-emerald-500 text-white shadow-sm'
                                  : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                              }`}
                              title={row.paid ? 'Pago — clique para marcar pendente' : 'Pendente — clique para marcar pago'}
                            >
                              {row.paid ? <Check className="w-3.5 h-3.5" /> : null}
                            </button>
                          </td>
                          <td className="px-2 py-2 text-right">
                            <button
                              type="button"
                              onClick={() => removeBreakdown(row.id)}
                              className="p-1 text-gray-300 hover:text-red-600 rounded transition-colors"
                              title="Remover"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}

              {/* Summary cards */}
              <div className="grid grid-cols-3 gap-3">
                <div className="px-3 py-2.5 rounded-lg border border-red-100 bg-red-50/60">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-red-600/70">Gasto</p>
                  <p className="text-base font-bold text-red-700 tabular-nums">{fmtEur(passthroughSpend)}</p>
                </div>
                <div className="px-3 py-2.5 rounded-lg border border-gray-200 bg-white">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Cobrado</p>
                  <p className="text-base font-bold text-gray-900 tabular-nums">{fmtEur(total)}</p>
                </div>
                <div className={`px-3 py-2.5 rounded-lg border ${passthroughNegative ? 'border-red-200 bg-red-50' : 'border-emerald-100 bg-emerald-50/60'}`}>
                  <p className={`text-[10px] font-semibold uppercase tracking-wider ${passthroughNegative ? 'text-red-600/70' : 'text-emerald-600/70'}`}>Comissão</p>
                  <p className={`text-base font-bold tabular-nums ${passthroughNegative ? 'text-red-700' : 'text-emerald-700'}`}>
                    {fmtEur(passthroughCommission)}
                  </p>
                </div>
              </div>
              {passthroughNegative && (
                <p
                  data-required-field="pl-item-passthrough"
                  className="text-[11px] text-red-600 bg-red-50 border border-red-100 rounded-md px-2.5 py-1.5"
                >
                  Gasto excede o cobrado ao cliente. Ajuste os valores antes de guardar.
                </p>
              )}
            </div>
          )}

          {/* Vendor & Scheduling */}
          <div className="space-y-4">
            <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100 pb-2">
              Fornecedor & Datas
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Fornecedor</label>
                <input
                  className={inputCls}
                  placeholder="Nome do fornecedor"
                  value={form.vendor_name ?? ''}
                  onChange={(e) => set({ vendor_name: e.target.value || null })}
                />
              </div>
              <div>
                <label className={labelCls}>Contacto</label>
                <input
                  className={inputCls}
                  placeholder="Email ou telefone"
                  value={form.vendor_contact ?? ''}
                  onChange={(e) => set({ vendor_contact: e.target.value || null })}
                />
              </div>
              <div>
                <label className={labelCls}>Data Prevista</label>
                <input
                  type="date"
                  className={inputCls}
                  value={form.scheduled_date ?? ''}
                  onChange={(e) => set({ scheduled_date: e.target.value || null })}
                />
              </div>
              <div>
                <label className={labelCls}>Data Real</label>
                <input
                  type="date"
                  className={inputCls}
                  value={form.actual_date ?? ''}
                  onChange={(e) => set({ actual_date: e.target.value || null })}
                />
              </div>
            </div>
          </div>

          {/* Payment — for a pass-through this is the client side (charging
              the client); supplier payments are the "Pago" ticks above */}
          <div className="space-y-4">
              <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100 pb-2">
                {kind === 'passthrough' ? 'Cobrança ao cliente' : 'Pagamento'}
              </h3>
              {kind === 'passthrough' && (
                <p className="text-[11px] text-gray-400 -mt-2">
                  Estado do valor cobrado ao cliente. Os pagamentos aos fornecedores marcam-se na coluna Pago do breakdown.
                </p>
              )}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className={labelCls}>Estado</label>
                  <select
                    className={inputCls}
                    value={form.payment_status}
                    onChange={(e) => set({ payment_status: e.target.value as ProjectoItemPaymentStatus })}
                  >
                    {PAYMENT_STATUSES.map((s) => <option key={s} value={s}>{PAYMENT_STATUS_LABELS[s]}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Pagamento Previsto</label>
                  <input
                    type="date"
                    className={inputCls}
                    value={form.expected_payment_date ?? ''}
                    onChange={(e) => set({ expected_payment_date: e.target.value || null })}
                  />
                </div>
                <div>
                  <label className={labelCls}>Pagamento Real</label>
                  <input
                    type="date"
                    className={inputCls}
                    value={form.actual_payment_date ?? ''}
                    onChange={(e) => set({ actual_payment_date: e.target.value || null })}
                  />
                </div>
              </div>
            </div>

          {/* Notes & Link */}
          <div className="space-y-4">
            <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100 pb-2">
              Notas
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Link</label>
                <input
                  className={inputCls}
                  placeholder="https://..."
                  value={form.link ?? ''}
                  onChange={(e) => set({ link: e.target.value || null })}
                />
              </div>
              <div>
                <label className={labelCls}>Observações</label>
                <textarea
                  rows={2}
                  className={`${inputCls} resize-none`}
                  placeholder="Notas sobre este item..."
                  value={form.observations ?? ''}
                  onChange={(e) => set({ observations: e.target.value || null })}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50 rounded-b-2xl">
          {/* Say why the button is inactive instead of just greying it out */}
          {!(form.name ?? '').trim() && (
            <span className="mr-auto text-xs text-gray-500">Preenche o Nome para guardar.</span>
          )}
          {(form.name ?? '').trim() && kind === 'passthrough' && passthroughNegative && (
            <span className="mr-auto text-xs text-red-600">O detalhe do pass-through excede o valor total.</span>
          )}
          <Button variant="secondary" size="sm" onClick={onClose}>Cancelar</Button>
          <Button
            variant="primary"
            size="sm"
            isLoading={saving}
            onClick={handleSave}
          >
            {item ? 'Guardar Alterações' : 'Adicionar Item'}
          </Button>
        </div>
      </div>
    </div>
  );
}
